/**
 * Імена та відмінки. Автоматика лише пропонує форму — будь-яку з них
 * діловод може виправити вручну, і виправлення зберігається за людиною.
 */
const up = (s) => (s || '').toUpperCase();
/** «Д.» — скорочення замість повного імені. */
export const isInitial = (w) => /^\p{Lu}\.$/u.test((w || '').trim());
/** Прізвище у родовому: КУЛІБАБИ, ШЕВЧЕНКА. */
export function surnameGen(w) {
    if (!w)
        return '';
    if (/ий$/.test(w))
        return w.slice(0, -2) + 'ого';
    if (/а$/.test(w))
        return w.slice(0, -1) + 'и';
    if (/я$/.test(w))
        return w.slice(0, -1) + 'ї';
    if (/о$/.test(w))
        return w.slice(0, -1) + 'а';
    if (/[ьй]$/.test(w))
        return w.slice(0, -1) + 'я';
    return w + 'а';
}
/** Прізвище у давальному: КУЛІБАБІ, ШЕВЧЕНКУ. */
export function surnameDat(w) {
    if (!w)
        return '';
    if (/ий$/.test(w))
        return w.slice(0, -2) + 'ому';
    if (/а$/.test(w))
        return w.slice(0, -1) + 'і';
    if (/я$/.test(w))
        return w.slice(0, -1) + 'ї';
    if (/о$/.test(w))
        return w.slice(0, -1) + 'у';
    if (/[ьй]$/.test(w))
        return w.slice(0, -1) + 'ю';
    return w + 'у';
}
/** Ім'я у давальному: Данилу, Сергію, Ганні. Ініціал («Д.») лишаємо як є. */
export function firstNameDat(w) {
    if (!w || isInitial(w))
        return w || '';
    if (/о$/.test(w))
        return w.slice(0, -1) + 'у';
    if (/[йь]$/.test(w))
        return w.slice(0, -1) + 'ю';
    if (/я$/.test(w))
        return w.slice(0, -1) + 'ї';
    if (/а$/.test(w))
        return w.slice(0, -1) + 'і';
    return w + 'у';
}
/** По батькові у давальному: Руслановичу, Іванівні. */
export function patronymicDat(w) {
    if (!w || isInitial(w))
        return w || '';
    if (/на$/.test(w))
        return w.slice(0, -1) + 'і';
    return w + 'у';
}
/**
 * Стать з по батькові або імені. По батькові каже однозначно; якщо його немає —
 * ім'я на «-а/-я» (Ілля, Микита — винятки).
 */
export function guessSex(p) {
    const patronymic = (p.patronymic ?? '').trim();
    if (patronymic && !isInitial(patronymic))
        return /(вна|чна)$/i.test(patronymic) ? 'f' : 'm';
    const first = (p.firstName ?? '').trim();
    if (!first || isInitial(first))
        return 'm';
    const female = /[аяі]$/i.test(first) && !/^(ілля|микита|кузьма|хома|сава|лука|мікіта)$/i.test(first);
    return female ? 'f' : 'm';
}
/** Збережена стать, або здогадка з ПІБ, якщо поле ще порожнє. */
export function sexOf(p) {
    return p.sex === 'm' || p.sex === 'f' ? p.sex : guessSex(p);
}
export function isFemale(p) {
    return sexOf(p) === 'f';
}
/** Форма слова за статтю: «ознайомлений» / «ознайомлена». */
export const byGender = (p, male, female) => (isFemale(p) ? female : male);
export const SEX_LABEL = { m: 'Чоловік', f: 'Жінка' };
export const initials = (p) => [p.firstName, p.patronymic]
    .filter(Boolean)
    .map(s => (isInitial(s) ? s.trim() : s.trim().charAt(0).toUpperCase() + '.'))
    .join('');
/** «Іваненко І.І.» — як у таблиці. */
export const shortName = (p) => [p.lastName, initials(p)].filter(Boolean).join(' ').trim();
/** «Іваненко Іван Іванович» */
export const fullName = (p) => [p.lastName, p.firstName, p.patronymic].filter(Boolean).join(' ').trim();
export function autoForms(p) {
    const sn = (p.lastName || '').trim();
    const f = (p.firstName || '').trim();
    const pt = (p.patronymic || '').trim();
    return {
        dat: [up(surnameDat(sn)), firstNameDat(f), patronymicDat(pt)].filter(Boolean).join(' '),
        nom: [f, up(sn)].filter(Boolean).join(' '),
        gen: (up(surnameGen(sn)) + ' ' + initials({ firstName: f, patronymic: pt })).trim(),
    };
}
/** Форми з урахуванням ручних виправлень людини. */
export function nameForms(p) {
    const a = autoForms(p);
    return {
        dat: p.declensions?.dat || a.dat,
        nom: p.declensions?.nom || a.nom,
        gen: p.declensions?.gen || a.gen,
    };
}
export const rankOf = (ranks, code) => ranks.find(r => r.code === code);
export function rankForm(ranks, code, form) {
    return rankOf(ranks, code)?.[form] ?? code ?? '';
}
/* ------------------------------------------------------- порядок за азбукою */
/**
 * Українська азбука. Стандартне сортування рядків ставить «і», «ї», «є» і «ґ»
 * після «я» — за номерами в Unicode вони саме там, — тож порядок доводиться
 * задавати самим.
 */
const ALPHABET = 'абвгґдеєжзиіїйклмнопрстуфхцчшщьюя';
const LETTER = new Map([...ALPHABET].map((c, i) => [c, i]));
/** Рядок як послідовність місць у азбуці; чуже й розділові знаки пропускаємо. */
export function ukKey(s) {
    const out = [];
    for (const ch of (s || '').toLowerCase().replace(/[’'`ʼ]/g, '')) {
        const i = LETTER.get(ch);
        if (i !== undefined)
            out.push(i);
    }
    return out;
}
/** Порівняння двох рядків за азбукою: коротший наперед за рівних початків. */
export function cmpUk(a, b) {
    const x = ukKey(a);
    const y = ukKey(b);
    const n = Math.min(x.length, y.length);
    for (let i = 0; i < n; i++)
        if (x[i] !== y[i])
            return x[i] - y[i];
    return x.length - y.length;
}
/**
 * Порядок людей у списку — за прізвищем, іменем і по батькові.
 *
 * Один на застосунок: список особового складу впорядкований завжди однаково,
 * і переставляти його руками не можна. Так діловод, командир і бот бачать
 * той самий порядок, а рядок людини не «переїжджає» між екранами.
 */
export const byName = (a, b) => cmpUk(a.lastName, b.lastName) ||
    cmpUk(a.firstName, b.firstName) ||
    cmpUk(a.patronymic, b.patronymic);
/**
 * Фільтр «Сержанти»: хто в нього потрапляє і в якому порядку.
 *
 *   старшина курсу
 *   далі по кожній групі: командир групи, командир І відділення, ІІ, …,
 *   і «вільні» сержанти — звання є, командирської посади немає
 *
 * Звичайні списки лишаються за абеткою — цей порядок діє лише у фільтрі.
 * Командир відділення пізнається за кодом посади `sqN`, як і в решті
 * програми. Сержантом вважається кожен, чиє звання вище найнижчого в
 * довіднику: звання налаштовуються, тож назви тут не зашиті.
 */
export function commandOrder(units, ranks) {
    const unitName = new Map(units.map(u => [u.id, u.name]));
    const lowest = Math.min(...ranks.map(r => r.ord));
    const rankOrd = new Map(ranks.map(r => [r.code, r.ord]));
    /** −1 — старшина, 0 — командир групи, N — командир N-го відділення, null — не командир. */
    const post = (p) => {
        if (p.positionCode === 'master')
            return -1;
        if (p.positionCode === 'platoon')
            return 0;
        const sq = /^sq(\d+)$/.exec(p.positionCode ?? '');
        if (sq)
            return Number(sq[1]);
        return (rankOrd.get(p.rankCode) ?? lowest) > lowest ? 1000 : null;
    };
    const unitOf = (p) => unitName.get(p.unitId ?? '') ?? '';
    return {
        has: p => post(p) !== null,
        sort: (a, b) => Number(post(b) === -1) - Number(post(a) === -1) ||
            unitOf(a).localeCompare(unitOf(b), 'uk', { numeric: true }) ||
            (post(a) ?? 0) - (post(b) ?? 0) ||
            byName(a, b),
    };
}
/** Нормалізація для пошуку: без регістру, апострофів і пробілів. */
export const searchKey = (s) => (s || '').toLowerCase().replace(/[’'`ʼ]/g, '').replace(/[^\p{L}\p{N}]/gu, '');
/** Розібрати «Іваненко Іван Іванович» або «Іваненко І.І.» на частини. */
export function parseName(raw) {
    const s = (raw || '').replace(/ /g, ' ').trim();
    const m = s.match(/^(\S+)\s+([А-ЯІЇЄҐA-Z])\.\s*([А-ЯІЇЄҐA-Z])\.$/u);
    if (m)
        return { lastName: m[1], firstName: m[2] + '.', patronymic: m[3] + '.' };
    const parts = s.split(/\s+/);
    return {
        lastName: parts[0] ?? '',
        firstName: parts[1] ?? '',
        patronymic: parts.slice(2).join(' '),
    };
}
/**
 * Посада у давальному: «Начальник інституту» -> «Начальнику інституту».
 * Це підказка для форми, а не заміна редагуванню — діловод завжди може виправити.
 */
export function positionDative(position) {
    const s = (position || '').trim();
    if (!s)
        return '';
    // «Т.в.о. начальника ІСЗЗІ КПІ» вже стоїть у формі, потрібній для звертання:
    // саме «Т.в.о.» тут головне слово, і воно не відмінюється.
    if (/^(?:т\.?\s*в\.?\s*о\.?|в\.?\s*о\.?)[\s.]/i.test(s))
        return s;
    // Межу слова пишемо через «далі не літера»: \b знає лише латиницю, і після
    // кирилиці її не буває — з нею жодне правило нижче не спрацьовувало б.
    const map = [
        [/^Начальник(?![\p{L}])/iu, 'Начальнику'],
        [/^Перший заступник(?![\p{L}])/iu, 'Першому заступнику'],
        [/^Заступник(?![\p{L}])/iu, 'Заступнику'],
        [/^Командир(?![\p{L}])/iu, 'Командиру'],
        [/^Старшина(?![\p{L}])/iu, 'Старшині'],
        [/^Лікар(?![\p{L}])/iu, 'Лікарю'],
        [/^Курсовий офіцер(?![\p{L}])/iu, 'Курсовому офіцеру'],
        [/^Курсант(?![\p{L}])/iu, 'Курсанту'],
    ];
    for (const [re, replacement] of map) {
        if (re.test(s))
            return s.replace(re, replacement);
    }
    return s;
}
/**
 * Ділить кожну групу навпіл за порядком списку: перша половина — І відділення,
 * друга — ІІ. Непарна людина йде в перше. Командир відділення (посада `sqN`)
 * лишається у своєму, навіть якщо стоїть в іншій половині. Без групи — пропускаємо.
 */
export function splitSquadsByGroup(people, unitId) {
    const groups = new Map();
    for (const p of people) {
        if (!p.unitId)
            continue;
        if (unitId && p.unitId !== unitId)
            continue;
        const list = groups.get(p.unitId) ?? [];
        list.push(p);
        groups.set(p.unitId, list);
    }
    const next = [];
    for (const list of groups.values()) {
        const mid = Math.ceil(list.length / 2);
        for (const [i, p] of list.entries()) {
            const fromPos = Number(/^sq(\d+)$/.exec(p.positionCode ?? '')?.[1] ?? 0);
            const squad = fromPos || (i < mid ? 1 : 2);
            if (p.squad !== squad)
                next.push({ id: p.id, squad });
        }
    }
    return next;
}
/**
 * Ініціали посадовця складаються з імені та по батькові — окремого поля немає,
 * щоб вони не розходилися з ПІБ. Саме тому в документі стоїть «О.О.», а не «О.».
 */
export function officialInitials(o) {
    return [o.firstName, o.patronymic]
        .map(x => (x ?? '').trim())
        .filter(Boolean)
        .map(x => (isInitial(x) ? x : x.charAt(0).toUpperCase() + '.'))
        .join('');
}
/** Нумерацію, крапки й дефіси на початку рядка в чатах ставлять хто як. */
const stripBullet = (l) => l.replace(/^[\s\u00a0]*(?:\d+[.)]?|[-—•*])?[\s\u00a0]*/, '').trim();
/** «С-21», «С21», «1 взвод» — короткий рядок без ознак ПІБ. */
const looksLikeUnit = (line) => line.length <= 20 && !/^[А-ЯЄІЇҐA-Z][а-яєіїґa-z'’-]+\s+[А-ЯЄІЇҐA-Z]/.test(line);
/**
 * Розбір списку, вставленого повідомленням.
 *
 * Списки надходять із месенджера в одному вигляді: назва групи, порожній рядок
 * і далі по людині на рядок. Нумерацію, крапки й дефіси на початку рядка
 * прибираємо — у чатах їх ставлять хто як.
 */
export function parsePeopleList(text) {
    const lines = text.split(/\r?\n/).map(stripBullet).filter(Boolean);
    let unit = null;
    if (lines.length && looksLikeUnit(lines[0]))
        unit = lines.shift();
    const names = [];
    const skipped = [];
    for (const line of lines) {
        // Три слова — ПІБ, два — прізвище та ім'я. Одне слово ПІБ не буває.
        const parts = line.split(/[\s ]+/).filter(Boolean);
        if (parts.length < 2 || parts.length > 4) {
            skipped.push(line);
            continue;
        }
        const [lastName, firstName, patronymic] = parts;
        names.push({
            raw: line,
            lastName: lastName ?? '',
            firstName: firstName ?? '',
            patronymic: patronymic ?? '',
        });
    }
    return { unit, names, skipped };
}
/**
 * Список зросту: хто в групі вищий за 190 см.
 *
 * Стройова частина подає його не так, як список групи, — здебільшого самими
 * прізвищами. Тому рядок з одного слова тут не помилка, а звичайна справа;
 * ім'я, якщо його все ж вписали, знадобиться хіба щоб розрізнити однофамільців.
 * Назву групи першим рядком тут не шукаємо: одне слово в цьому списку —
 * прізвище, і «С-21» від «Бегура» відрізнити вже нічим.
 *
 * Зайве в рядку («192», «см») мовчки відкидаємо: у чаті зріст часто дописують
 * поруч із прізвищем, і рядок від цього не перестає бути рядком людини.
 */
export function parseTallList(text) {
    const names = [];
    const skipped = [];
    for (const line of text.split(/\r?\n/).map(stripBullet).filter(Boolean)) {
        const words = line.split(/[\s\u00a0]+/).filter(w => /^\p{L}/u.test(w));
        if (!words.length || words.length > 4) {
            skipped.push(line);
            continue;
        }
        names.push({
            raw: line,
            lastName: words[0] ?? '',
            firstName: words[1] ?? '',
            patronymic: words[2] ?? '',
        });
    }
    return { names, skipped };
}
/**
 * Ключ для звірки людини зі списком: прізвище та перша літера імені.
 *
 * По батькові в різних списках пишуть по-різному — то повністю, то ініціалом,
 * — тож у ключ воно не входить. Прізвища з дефісом і апострофом зводимо до
 * простого вигляду, бо в чаті їх набирають різними символами.
 */
export const peopleKey = (p) => `${searchKey(p.lastName ?? '')}|${searchKey(p.firstName ?? '').slice(0, 1)}`;
//# sourceMappingURL=people.js.map